import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { createHubConnection, stopHubConnection } from "../services/signalr";
import { SIGNALR_CONFIG } from "../constants/signalr";
import { toast } from "react-toastify";
import { addNotification } from "./notificationSlice";

export const connectSignalR = createAsyncThunk(
  "signalr/connect",
  async (_, { getState, rejectWithValue, dispatch }) => {
    const state = getState();
    const token = state.auth.token || localStorage.getItem("authToken");

    if (!token) {
      return rejectWithValue("No auth token");
    }

    if (!SIGNALR_CONFIG.HUB_URL) {
      return rejectWithValue(
        "SignalR not configured (missing VITE_SIGNALR_HUB_URL)",
      );
    }

    const connection = await createHubConnection(token);
    if (!connection) {
      return rejectWithValue("SignalR backend unavailable");
    }

    // Join groups based on user role
    const user = state.auth.user;
    if (user?.role === "Admin") {
      await connection.invoke("JoinGroup", SIGNALR_CONFIG.GROUPS.ADMIN);
    } else if (user?.role === "Employee") {
      await connection.invoke("JoinGroup", SIGNALR_CONFIG.GROUPS.EMPLOYEE);
    }

    // Listen to events
    connection.on(
      SIGNALR_CONFIG.EVENTS.RECEIVE_NOTIFICATION,
      (notification) => {
        toast.info(notification.message);
        dispatch(
          addNotification({
            message: notification.message,
            type: notification.type || "info",
            title: notification.title || "New Notification",
          }),
        );
      },
    );

    connection.on(SIGNALR_CONFIG.EVENTS.COMPANY_UPDATED, () => {
      // Refetch companies
      dispatch({ type: "company/fetchAllCompanies" });
      toast.info("Company list updated");
    });

    connection.on(SIGNALR_CONFIG.EVENTS.USER_UPDATED, () => {
      // Refetch users
      dispatch({ type: "companyUser/fetchAllCompanyUsers" });
      toast.info("User list updated");
    });

    return { connection: true };
  },
);

export const disconnectSignalR = createAsyncThunk(
  "signalr/disconnect",
  async () => {
    await stopHubConnection();
    return { connection: false };
  },
);

const signalrSlice = createSlice({
  name: "signalr",
  initialState: {
    isConnected: false,
    isConnecting: false,
    error: null,
  },
  reducers: {
    connectionStatusChanged: (state, action) => {
      state.isConnected = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(connectSignalR.pending, (state) => {
        state.isConnecting = true;
        state.error = null;
      })
      .addCase(connectSignalR.fulfilled, (state) => {
        state.isConnected = true;
        state.isConnecting = false;
      })
      .addCase(connectSignalR.rejected, (state, action) => {
        state.isConnected = false;
        state.isConnecting = false;
        state.error = action.payload;
      })
      .addCase(disconnectSignalR.fulfilled, (state) => {
        state.isConnected = false;
      });
  },
});

export const { connectionStatusChanged } = signalrSlice.actions;
export default signalrSlice.reducer;
